import {
  Building2,
  CalendarCheck,
  Heart,
  Home,
  Info,
  LayoutDashboard,
  LogIn,
  Map as MapIcon,
  MapPin,
  MessageCircle,
  MoreHorizontal,
  Search,
  SquarePlus,
  UserCog,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router';
import { formatBadge, useUnreadCount } from '../../chat/hooks/useUnreadCount';
import { cn } from '../../pro/lib/cn';
import { BottomSheet } from '../../pro/ui/overlays';
import { useAuthStore } from '../../store/useAuthStore';

/*
 * The phone tab bar of the whole site with TURAQ Pro on — the same bar as the
 * cabinet's (CabinetLayout): four tabs and «Ещё» with everything else. One
 * look everywhere instead of the round pill outside the cabinet and a flat
 * bar inside it. The cabinet hides this one (body.pro-cabinet) and shows its
 * own tabs. BottomNav.tsx stays for builds with Pro off.
 */

type Item = { to: string; label: string; icon: LucideIcon; badge?: string | null };

function useTyping() {
  // Keyboard up (a text field focused): the bar steps aside, as BottomNav did.
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isField = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
      if (el.tagName !== 'INPUT') return false;
      return !['checkbox', 'radio', 'range', 'button', 'submit', 'file', 'color'].includes((el as HTMLInputElement).type);
    };
    const onIn = (e: FocusEvent) => isField(e.target) && setTyping(true);
    const onOut = () => setTimeout(() => setTyping(isField(document.activeElement)), 0);
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, []);
  return typing;
}

export function ProTabBar({ mobileMenuOpen }: { mobileMenuOpen: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const authed = useAuthStore((s) => s.isAuthenticated);
  const unread = formatBadge(useUnreadCount());
  const typing = useTyping();
  const [more, setMore] = useState(false);
  useEffect(() => setMore(false), [pathname]);

  // Page paddings (pb-bottom-nav, toasts) follow this bar's height.
  useEffect(() => {
    document.documentElement.classList.add('pro-tabbar');
    return () => document.documentElement.classList.remove('pro-tabbar');
  }, []);

  const tabs: Item[] = [
    { to: '/', label: t('nav.home', 'Главная'), icon: Home },
    { to: '/listings', label: t('nav.browse', 'Объявления'), icon: Search },
    { to: '/create-listing', label: t('nav.post', 'Разместить'), icon: SquarePlus },
    { to: '/messages', label: t('nav.messages', 'Сообщения'), icon: MessageCircle, badge: unread },
  ];
  const rest: Item[] = [
    ...(authed
      ? [
          { to: '/dashboard', label: t('nav.dashboard', 'Кабинет'), icon: LayoutDashboard },
          { to: '/dashboard/account', label: t('nav.account', 'Аккаунт'), icon: UserCog },
          { to: '/bookings', label: t('nav.myRequests', 'Мои заявки'), icon: CalendarCheck },
        ]
      : [
          { to: '/login', label: t('nav.login', 'Войти'), icon: LogIn },
          { to: '/register', label: t('nav.register', 'Регистрация'), icon: UserPlus },
        ]),
    { to: '/favorites', label: t('nav.favorites', 'Избранное'), icon: Heart },
    { to: '/novostroyki', label: t('nav.newBuildings', 'Новостройки'), icon: Building2 },
    { to: '/specialists', label: t('nav.specialists', 'Специалисты'), icon: Users },
    { to: '/map-page', label: t('nav.kyrgyzMap', 'На карте'), icon: MapPin },
    { to: '/turaq-kg-genplan-bishkek2050', label: t('nav.genplan', 'Генплан'), icon: MapIcon },
    { to: '/aboutus', label: t('nav.aboutUs', 'О нас'), icon: Info },
  ];
  const moreActive = rest.some((i) => pathname === i.to || (i.to !== '/dashboard' && pathname.startsWith(`${i.to}/`)));

  return (
    <div
      data-chrome-wrap="bottom-nav"
      className={cn(
        'fixed inset-x-0 bottom-0 z-[60] transition-transform duration-300 ease-out md:hidden',
        mobileMenuOpen || typing ? 'pointer-events-none translate-y-full opacity-0' : 'translate-y-0 opacity-100',
      )}
    >
      <nav
        data-chrome="bottom-nav"
        aria-label={t('nav.mobileNavigation', 'Мобильная навигация')}
        className="p-glass border-t border-p-line"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <ul className="mx-auto flex max-w-lg">
          {tabs.map((i) => (
            <li key={i.to} className="flex-1">
              <NavLink
                to={i.to}
                end={i.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'p-focus relative flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-semibold',
                    isActive || (i.to === '/listings' && /^\/(property|listing)\//.test(pathname)) ? 'text-p-primary-ink' : 'text-p-muted',
                  )
                }
              >
                <i.icon className="size-6" />
                <span className="max-w-full truncate px-1">{i.label}</span>
                {i.badge ? <span className="p-num absolute left-1/2 top-2 ml-2 rounded-full bg-p-danger px-1.5 text-[10.5px] font-bold text-white">{i.badge}</span> : null}
              </NavLink>
            </li>
          ))}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => setMore(true)}
              aria-haspopup="dialog"
              className={cn('p-focus relative flex h-16 w-full flex-col items-center justify-center gap-1 text-[11px] font-semibold', moreActive ? 'text-p-primary-ink' : 'text-p-muted')}
            >
              <MoreHorizontal className="size-6" />
              {t('nav.more', 'Ещё')}
            </button>
          </li>
        </ul>
      </nav>
      <BottomSheet open={more} onOpenChange={setMore} title={t('nav.more', 'Ещё')}>
        <div className="grid grid-cols-3 gap-2">
          {rest.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              onClick={() => setMore(false)}
              className={cn(
                'flex flex-col items-center gap-2 rounded-2xl border p-3 text-center text-[13px] font-semibold',
                pathname === i.to ? 'border-p-primary bg-p-primary-soft text-p-primary-ink' : 'border-p-line text-p-ink-2',
              )}
            >
              <i.icon className="size-6" />
              {i.label}
            </Link>
          ))}
        </div>
      </BottomSheet>
    </div>
  );
}
